import { type Partner, type Product } from '@mzc-watchover/adminapp-appsync'
import {
  compact,
  filter,
  find,
  groupBy,
  includes,
  isArray,
  isEmpty,
  isNil,
  isString,
  join,
  keys,
  map,
  size,
  split,
  trim,
  xor
} from 'lodash/fp'
import {
  ArrayField,
  Datagrid,
  email,
  Form,
  type Identifier,
  Pagination,
  type RaRecord,
  required,
  type RowClickFunction,
  TextField,
  TextInput,
  useTranslate
} from 'react-admin'
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Grid,
  Typography
} from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { useGetPartner } from 'components/DataProvider/hooks'
import React from 'react'
import { assign } from 'lodash'
// import log from 'loglevel'

type Organization = {
  contactOrganization: string
  contactEmail: string
  indices: number[]
}

export const OrganizationsEdit: React.FC<{
  partner: Partial<Partner> | undefined
}> = ({ partner: partnerArg }) => {
  const translate = useTranslate()
  const { data: partner, isLoading } = useGetPartner(
    isNil(partnerArg) ? undefined : partnerArg.id
  )
  const [organizations, setOrgnizations] = React.useState<
    Organization[] | undefined
  >(undefined)
  React.useEffect(() => {
    if (isLoading || isNil(partner) || !isNil(organizations)) return
    const { products } = partner
    const byOrganization = groupBy<Product & { index: number }>(
      (item: Product & { index: number }) => {
        const { contactOrganization, contactEmail } = item
        return JSON.stringify({ contactOrganization, contactEmail })
      }
    )(
      compact(
        // @ts-expect-error ts(2339)
        map.convert({ cap: false })((item: Product, index: number) => {
          if (
            isNil(item.contactOrganization) ||
            isEmpty(item.contactOrganization)
          )
            return undefined
          return { ...item, index }
        })(products)
      )
    )
    const items = map((key: string) => {
      const { contactOrganization, contactEmail } = JSON.parse(key)
      return {
        contactOrganization,
        contactEmail,
        indices: map((item: { index: number }) => item.index)(
          byOrganization[key]
        )
      }
    })(keys(byOrganization))
    setOrgnizations(items)
  }, [isLoading, partner, organizations])
  const transform = (_id: string, _data: Organization[]) => () => {}
  if (isLoading || isNil(partner)) return null
  const { id } = partner
  return (
    <Card raised sx={{ width: '80%', marginBottom: '1em' }}>
      <CardContent>
        <Form
          noValidate
          mode="onChange"
          reValidateMode="onChange"
          defaultValues={{
            organizations,
            updates: []
          }}>
          <Grid container>
            <Grid item xs={12} sx={{ mb: '0.5em' }}>
              <Typography variant="h6">
                {translate('resources.organizationsEdit.title')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <OrganizationListInput />
            </Grid>
          </Grid>
          <SubmitButton id={id} onSubmit={transform} />
        </Form>
      </CardContent>
    </Card>
  )
}
const SubmitButton: React.FC<{
  id: string
  onSubmit: (id: string, data: Organization[]) => () => void
}> = ({ id, onSubmit }) => {
  const translate = useTranslate()
  const { getValues } = useFormContext()
  const { organizations } = getValues()
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      onClick={onSubmit(id, organizations)}>
      <Typography variant="button">
        {translate('resources.contactEdit.buttons.save')}
      </Typography>
    </Button>
  )
}
const OrganizationListInput: React.FC = () => {
  const translate = useTranslate()
  const { setValue, getValues } = useFormContext()
  const [open, setOpen] = React.useState(false)
  const [perPage, setPerPage] = React.useState(25)
  const [record, setRecord] = React.useState<Organization | undefined>(
    undefined
  )
  const editOrganization: RowClickFunction = (
    _id: Identifier,
    _resource: string,
    record: RaRecord<Identifier>
  ): any => {
    setRecord(record as any)
    setOpen(true)
  }
  const organizationsError = []
  const { organizations, updates } = getValues()
  const organizationFilter = useWatch({ name: 'organizationFilter' })
  const emailFilter = useWatch({ name: 'emailFilter' })
  React.useEffect(() => {
    let filtered = organizations
    if (size(organizationFilter) > 2) {
      filtered = filter((item: { contactOrganization: string }) =>
        includes(organizationFilter)(item.contactOrganization)
      )(filtered)
    }
    if (size(emailFilter) > 2) {
      filtered = filter((item: { contactEmail: string }) =>
        includes(emailFilter)(item.contactEmail)
      )(filtered)
    }
    setValue('target', filtered, { shouldDirty: true, shouldValidate: true })
  }, [organizations, organizationFilter, emailFilter])
  return (
    <div>
      <Grid container>
        <Grid item xs={12} sx={{ ml: '0.5em', mb: '-0.5em' }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextInput
                source="organizationFilter"
                label="resources.organizationsEdit.fields.organizationFilter"
                fullWidth
                resettable
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                source="emailFilter"
                label="resources.organizationsEdit.fields.emailFilter"
                fullWidth
                resettable
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <ArrayField source="target" perPage={perPage} record={getValues()}>
            <Datagrid
              resource="data"
              bulkActionButtons={false}
              rowClick={editOrganization}
              sx={{
                '& .column-contactOrganization': {
                  width: '50%'
                }
              }}>
              <TextField
                source="contactOrganization"
                label="resources.organizationsEdit.fields.contactOrganization"
              />
              <TextField
                source="contactEmail"
                label="resources.organizationsEdit.fields.contactEmail"
              />
            </Datagrid>
            <Pagination perPage={perPage} setPerPage={setPerPage} />
          </ArrayField>
        </Grid>
        <Grid item xs={12} sx={{ mb: '2em', ml: '0.5em' }}>
          <Box>
            {!isNil(organizationsError) &&
              (isArray(organizationsError) ? (
                map((item: any) => {
                  const key = keys(item)[0] as string
                  const value = item[key]
                  if (!isNil(key)) {
                    return (
                      <Typography color="red" variant="body2" key={key}>
                        {translate(
                          `resources.partners.fields.productsForEdit.organizations.${key}`
                        )}
                        : {translate(value)}
                      </Typography>
                    )
                  } else return <></>
                })(organizationsError)
              ) : (
                <Typography color="red" variant="body2">
                  {translate(organizationsError)}
                </Typography>
              ))}
          </Box>
        </Grid>
      </Grid>
      {open && !isNil(record) && (
        <OrganizationDialog
          open={open}
          setOpen={setOpen}
          record={record}
          updates={updates}
        />
      )}
    </div>
  )
}
const OrganizationDialog: React.FC<{
  open: boolean
  setOpen: (state: boolean) => void
  record: Organization
  updates: Organization[]
}> = ({ open, setOpen, record, updates }) => {
  const [errors, setErrors] = React.useState<
    Record<string, string> | undefined
  >(undefined)
  const translate = useTranslate()
  const { getValues, setValue } = useFormContext()
  const handleClose = (): void => {
    setOpen(false)
  }
  const handleDone = (): void => {
    const { forms } = getValues()
    const { contactOrganization, contactEmail } = forms
    const emailErrors = compact(
      map((item: string) => {
        const trimed = trim(item)
        return email(`メールアドレスの形式が異常です: ${trimed}`)(trimed)
      })(split(',')(contactEmail))
    )
    const errors = {}
    if (!isEmpty(emailErrors)) {
      const message = join(',')(
        compact(
          map((error: any) => (isString(error) ? error : error.message))(
            emailErrors
          )
        )
      )
      assign(errors, { email: message })
    }
    if (!isEmpty(errors)) {
      setErrors(errors)
      return
    }
    if (
      contactOrganization !== record.contactOrganization ||
      contactEmail !== record.contactEmail
    ) {
      const { indices } = record
      updates.push({ contactOrganization, contactEmail, indices })
      setValue('updates', updates)
      const { organizations } = getValues()
      const item = find((item: Organization) =>
        isEmpty(xor(item.indices)(indices))
      )(organizations)
      if (!isNil(item)) {
        item.contactOrganization = contactOrganization
        item.contactEmail = contactEmail
      }

      setValue('organizations', organizations)
    }
    setOpen(false)
  }
  const cancel = (): void => {
    setOpen(false)
  }
  React.useEffect(() => {
    setValue('forms', record)
  }, [record])
  return (
    <>
      <Dialog onClose={handleClose} open={open} maxWidth="xl" fullWidth>
        <DialogContent>
          <TextInput
            source="forms.contactOrganization"
            label={`resources.organizationsEdit.fields.contactOrganization`}
            validate={required()}
            fullWidth
          />
          <TextInput
            source="forms.contactEmail"
            label={`resources.organizationsEdit.fields.contactEmail`}
            validate={required()}
            fullWidth
          />
          <Grid container>
            <Grid item xs={1} sx={{ mb: '2em', ml: '1em' }}>
              <Button variant="outlined" onClick={cancel}>
                {translate(`resources.organizationsEdit.dialogs.cancel`)}
              </Button>
            </Grid>
            <Grid item xs={1} sx={{ mb: '2em', ml: '1em' }}>
              <Button variant="contained" onClick={handleDone}>
                {translate(`resources.organizationsEdit.dialogs.done`)}
              </Button>
            </Grid>
          </Grid>
          {!isNil(errors) && (
            <Typography color="red" variant="inherit">
              {join(', ')(map((key: string) => errors[key])(keys(errors)))}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
